import {
  Form,
  FormField,
  RequiredValidator,
  IFormField,
  FormLevelValidator,
} from '@thinknimble/tn-forms'
import i18next from '../../i18n/config'
import { PhoneNumberValidator } from 'src/utils/phone'
import { vendorTypeEnum } from './models'

/**
 * Cross-field validator that requires organizations when vendor type is 'individual'.
 * Uses the matcher to reference vendorType field and validates organizations value.
 */
export class RequiredOrganizationsForIndividualValidator extends FormLevelValidator {
  constructor({ message }: { message: string }) {
    super({
      message,
      code: 'requiredOrganizationsForIndividual',
      isRequired: false,
      matcher: 'vendorType',
    })
  }

  call(value: string[] | undefined) {
    // matchingVal is the vendorType field value
    if (this.matchingVal === vendorTypeEnum.INDIVIDUAL) {
      if (!value || value.length === 0) {
        throw new Error(
          JSON.stringify({
            code: this.code,
            message: this.message,
          }),
        )
      }
    }
  }
}

export const vendorFormLevelValidators = {
  organizations: new RequiredOrganizationsForIndividualValidator({
    message: 'Individual vendors must be associated with at least one organization.',
  }),
}

export type VendorFormInputs = {
  _name: IFormField<string>
  displayName: IFormField<string>
  vendorType: IFormField<string>
  taxId: IFormField<string>
  identificationNumber: IFormField<string>
  identificationNumberType: IFormField<string | null>
  taxIdType: IFormField<string | null>
  email: IFormField<string>
  phone: IFormField<string>
  website: IFormField<string>
  addressLine1: IFormField<string>
  addressLine2: IFormField<string>
  city: IFormField<string>
  stateProvince: IFormField<string>
  postalCode: IFormField<string>
  country: IFormField<string>
  currencyCode: IFormField<string>
  requires1099: IFormField<boolean>
  accountNumber: IFormField<string>
  isActive: IFormField<boolean>
  withholdingRate: IFormField<string>
  notes: IFormField<string>
  organizations: IFormField<string[]>
}

export class VendorForm extends Form<VendorFormInputs> {
  static _name = new FormField({
    label: 'Vendor Name',
    placeholder: 'Enter vendor name',
    validators: [new RequiredValidator()],
    type: 'text',
  })

  static displayName = new FormField({
    label: 'Display Name',
    placeholder: 'Enter display name (optional)',
    validators: [],
    type: 'text',
  })

  static vendorType = new FormField({
    label: 'Vendor Type',
    placeholder: 'Select vendor type',
    validators: [new RequiredValidator()],
    type: 'option',
    value: 'business',
  })

  static taxId = new FormField({
    label: 'Tax ID',
    placeholder: 'Enter tax ID (NIT, SSN, EIN, etc.)',
    validators: [],
    type: 'text',
  })

  static identificationNumber = new FormField({
    label: 'Identification Number',
    placeholder: 'Enter identification number',
    validators: [],
    type: 'text',
  })

  static identificationNumberType = new FormField({
    label: 'ID Type',
    placeholder: 'Select ID type',
    validators: [],
    type: 'option',
  })

  static taxIdType = new FormField({
    label: 'Tax ID Type',
    placeholder: 'Select tax ID type',
    validators: [],
    type: 'option',
  })

  static email = new FormField({
    label: 'Email',
    placeholder: 'vendor@example.com',
    validators: [],
    type: 'text',
  })

  static phone = new FormField({
    label: 'Phone',
    placeholder: 'Enter phone number',
    validators: [
      new PhoneNumberValidator({ message: i18next.t('common.invalidPhone'), isRequired: false }),
    ],
    type: 'text',
  })

  static website = new FormField({
    label: 'Website',
    placeholder: 'https://example.com',
    validators: [],
    type: 'text',
  })

  static addressLine1 = new FormField({
    label: 'Address Line 1',
    placeholder: 'Street address',
    validators: [],
    type: 'text',
  })

  static addressLine2 = new FormField({
    label: 'Address Line 2',
    placeholder: 'Apt, suite, etc. (optional)',
    validators: [],
    type: 'text',
  })

  static city = new FormField({
    label: 'City',
    placeholder: 'City',
    validators: [],
    type: 'text',
  })

  static stateProvince = new FormField({
    label: 'State/Province',
    placeholder: 'State or province',
    validators: [],
    type: 'text',
  })

  static postalCode = new FormField({
    label: 'Postal Code',
    placeholder: 'Postal/ZIP code',
    validators: [],
    type: 'text',
  })

  static country = new FormField({
    label: 'Country',
    placeholder: 'Country code (e.g., US, CO)',
    validators: [],
    type: 'text',
  })

  static currencyCode = new FormField({
    label: 'Currency',
    placeholder: 'Currency code (e.g., USD, COP)',
    validators: [],
    type: 'text',
  })

  static requires1099 = new FormField({
    label: 'Requires 1099',
    validators: [],
    type: 'checkbox',
    value: false,
  })

  static accountNumber = new FormField({
    label: 'Account Number',
    placeholder: 'Internal account number',
    validators: [],
    type: 'text',
  })

  static isActive = new FormField({
    label: 'Active',
    validators: [],
    type: 'checkbox',
    value: true,
  })

  static withholdingRate = new FormField({
    label: 'Withholding Rate (%) / Tasa de Retención (%)',
    placeholder: 'e.g. 11 or 11.5',
    validators: [],
    type: 'text',
  })

  static notes = new FormField({
    label: 'Notes',
    placeholder: 'Additional notes',
    validators: [],
    type: 'long-text',
  })

  static organizations = new FormField({
    label: 'Organizations',
    placeholder: 'Select organizations',
    validators: [],
    type: 'multi-select',
    value: [],
  })

  get val() {
    return {
      name: this.field._name.value,
      displayName: this.field.displayName.value || null,
      vendorType: this.field.vendorType.value,
      taxId: this.field.taxId.value || null,
      identificationNumber: this.field.identificationNumber.value || null,
      identificationNumberType: this.field.identificationNumberType.value || null,
      taxIdType: this.field.taxIdType.value || null,
      email: this.field.email.value || null,
      phone: this.field.phone.value || null,
      website: this.field.website.value || null,
      addressLine1: this.field.addressLine1.value || null,
      addressLine2: this.field.addressLine2.value || null,
      city: this.field.city.value || null,
      stateProvince: this.field.stateProvince.value || null,
      postalCode: this.field.postalCode.value || null,
      country: this.field.country.value || null,
      currencyCode: this.field.currencyCode.value || null,
      requires1099: this.field.requires1099.value,
      accountNumber: this.field.accountNumber.value || null,
      isActive: this.field.isActive.value,
      withholdingRate: this.field.withholdingRate.value || null,
      notes: this.field.notes.value || null,
      organizations: this.field.organizations.value || [],
    }
  }
}

export type TVendorForm = VendorForm & VendorFormInputs
